import { useEffect, useRef } from 'react';
import { SebastianStatus } from '~/constants/common/SebastianStatus';
import {
  useAppliedFilters,
  useAppliedFiltersContext
} from '~/context/common/AppliedFiltersContext';
import { useSebastianStatus } from '~/context/common/SebastianContext';
import {
  useInventoryIsLoaded,
  useSearchRequestId,
  useTotalMatchedInventory,
  useUserDeliveryInfo
} from '~/context/srp/InventoryContext';
import type { AppliedFilters } from '~/contracts/common';
import { generateErrorLogger } from '~/utilities/loggers';

export type SrpPageContext = {
  filters: Partial<AppliedFilters>;
  resultCount: number;
  sort: string;
  zipCode?: string;
};

const logSetPageContextError = generateErrorLogger(
  'useSendSrpSebastianPageContext.setPageContext'
);

export default function useSendSrpSebastianPageContext() {
  const sebastianStatus = useSebastianStatus();
  const filters = useAppliedFilters();
  const { sortBy } = useAppliedFiltersContext();
  const isInventoryLoaded = useInventoryIsLoaded();
  const searchRequestId = useSearchRequestId();
  const resultCount = useTotalMatchedInventory();
  const { zip5: zipCode } = useUserDeliveryInfo() ?? {};
  const lastSentSearchRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      sebastianStatus !== SebastianStatus.Ready ||
      !isInventoryLoaded ||
      lastSentSearchRequestIdRef.current === searchRequestId
    )
      return;

    try {
      window.CarvanaChatUi?.setPageContext?.(
        {
          surface: 'SRP',
          surface_description: 'Customer is browsing the search results page'
        },
        {
          filters,
          resultCount,
          sort: sortBy,
          ...(zipCode && { zipCode })
        }
      );
      lastSentSearchRequestIdRef.current = searchRequestId;
    } catch (err) {
      logSetPageContextError(err);
    }
  }, [
    filters,
    isInventoryLoaded,
    resultCount,
    searchRequestId,
    sebastianStatus,
    sortBy,
    zipCode
  ]);
}
